/**
 * Coverage Map — ZIP search box (Phase F.2).
 *
 * Discovery-polish helper that lets a visitor type a 5-digit ZIP and:
 *   - If the ZIP is in `coveredZips`: call `onMatch(zip)` so the parent
 *     Map.jsx component flies the map to that polygon and opens the
 *     side-panel (existing handleZipClick path).
 *   - If the ZIP is valid 5-digit but NOT in `coveredZips`: surface a
 *     friendly "Want PooPluckers in 68155? Join the waitlist."
 *     inline pill that calls `onWaitlistRequest(zip)` (parent decides
 *     how to render the waitlist form — F.2 scrolls + autopopulates,
 *     F.4 will add per-ZIP recruiter copy).
 *   - If the ZIP is invalid (non-numeric / <5 digits): inline error.
 *
 * Privacy: ZIP-only input, ZIP-only callbacks. The component never
 * reads, stores, or transmits any address-shaped data.
 *
 * Nearest-covered hint: when a typed ZIP is uncovered, the component
 * suggests the nearest covered ZIP via client-side Haversine on the
 * GeoJSON centroid (computed once and memoised). The centroid math
 * lives in `lib/zipCentroid.js` — pure GeoJSON, no address access.
 */
import { useMemo, useRef, useState } from "react";
import { nearestCoveredZip } from "@/lib/zipCentroid";
import { API_BASE } from "@/lib/api";
import { trackRecruitEvent } from "@/lib/recruitTracking";

const ZIP_RE = /^\d{5}$/;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function ZipSearchBox({
  coveredZips,
  geojson,
  onMatch,
}) {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const coveredSet = useMemo(
    () => new Set(coveredZips || []),
    [coveredZips],
  );

  const [uncoveredSuggestion, setUncoveredSuggestion] = useState(null);
  // Inline waitlist email-collection state — surfaces only after the
  // user clicks the "Notify me…" CTA on an uncovered-ZIP result.
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [waitlistError, setWaitlistError] = useState("");

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    const v = (value || "").trim();
    if (!ZIP_RE.test(v)) {
      setError("Please enter a 5-digit ZIP.");
      setUncoveredSuggestion(null);
      return;
    }
    setError("");
    setWaitlistOpen(false);
    setWaitlistSubmitted(false);
    setWaitlistError("");
    if (coveredSet.has(v)) {
      setUncoveredSuggestion(null);
      onMatch?.(v);
      return;
    }
    const nearest = nearestCoveredZip({
      query: v,
      coveredZips: coveredSet,
      geojson,
    });
    setUncoveredSuggestion({ zip: v, nearest });
    // F.4 — funnel event: searched ZIP is uncovered. `source` lets
    // future variants distinguish e.g. header-search vs hero-search.
    trackRecruitEvent("search_zip_uncovered", { zip: v, source: "map_search" });
  };

  const onClear = () => {
    setValue("");
    setError("");
    setUncoveredSuggestion(null);
    setWaitlistOpen(false);
    setWaitlistSubmitted(false);
    setWaitlistError("");
    inputRef.current?.focus();
  };

  const submitWaitlist = async (e) => {
    e?.preventDefault?.();
    setWaitlistError("");
    if (!EMAIL_RE.test(waitlistEmail.trim())) {
      setWaitlistError("Please enter a valid email.");
      return;
    }
    if (!uncoveredSuggestion?.zip) return;
    setWaitlistSubmitting(true);
    try {
      const r = await fetch(`${API_BASE}/public/coverage-notify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: waitlistEmail.trim(),
          zip: uncoveredSuggestion.zip,
        }),
      });
      if (!r.ok) {
        let detail = "Couldn't add you right now. Please try again in a moment.";
        try {
          const j = await r.json();
          if (j?.detail) detail = String(j.detail);
        } catch {
          /* ignore */
        }
        throw new Error(detail);
      }
      setWaitlistSubmitted(true);
      // F.4 — funnel event: inline-search waitlist submitted.
      trackRecruitEvent("search_waitlist_submitted", {
        zip: uncoveredSuggestion?.zip,
        source: "map_search",
      });
    } catch (ex) {
      setWaitlistError(ex.message || "Something went wrong.");
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  return (
    <div
      className="rounded-2xl p-4 lg:p-5"
      style={{
        background: "var(--pp-v3-surface)",
        border: "1px solid var(--pp-line)",
      }}
      data-testid="map-zip-search-box"
    >
      <form
        onSubmit={handleSubmit}
        className="flex flex-col sm:flex-row gap-3 sm:items-center"
        data-testid="map-zip-search-form"
      >
        <label
          htmlFor="zip-search-input"
          className="pp-kick"
          style={{ fontSize: 11, minWidth: "fit-content" }}
        >
          Find your ZIP
        </label>
        <input
          id="zip-search-input"
          ref={inputRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={5}
          value={value}
          onChange={(e) => {
            // Strip non-numeric on input — keeps the live value clean
            // so paste of "68130-1234" lands as "68130".
            const v = e.target.value.replace(/\D/g, "").slice(0, 5);
            setValue(v);
            if (error) setError("");
          }}
          placeholder="e.g. 68130"
          aria-label="Enter a 5-digit ZIP to search coverage"
          data-testid="map-zip-search-input"
          className="rounded-md px-3 py-2 flex-1"
          style={{
            border: "1px solid var(--pp-line)",
            background: "var(--pp-bg)",
            color: "var(--pp-text)",
            fontFamily: "ui-monospace, SFMono-Regular, monospace",
            fontSize: 15,
            letterSpacing: "0.04em",
          }}
        />
        <button
          type="submit"
          data-testid="map-zip-search-submit"
          className="rounded-md px-4 py-2 font-semibold"
          style={{
            background: "var(--pp-orange)",
            color: "white",
            fontSize: 14,
          }}
        >
          Search
        </button>
        {(value || uncoveredSuggestion || error) && (
          <button
            type="button"
            onClick={onClear}
            data-testid="map-zip-search-clear"
            className="text-sm"
            style={{ color: "var(--pp-text-soft)" }}
          >
            Clear
          </button>
        )}
      </form>
      {error && (
        <div
          className="mt-2 text-sm"
          style={{ color: "#B91C1C" }}
          data-testid="map-zip-search-error"
        >
          {error}
        </div>
      )}
      {uncoveredSuggestion && !waitlistSubmitted && (
        <div
          className="mt-3 rounded-md p-3"
          style={{
            background: "rgba(248, 150, 35, 0.08)",
            border: "1px solid rgba(248, 150, 35, 0.25)",
          }}
          data-testid="map-zip-search-uncovered-pill"
        >
          <div className="text-sm" style={{ color: "var(--pp-text)" }}>
            We&rsquo;re not in <strong>{uncoveredSuggestion.zip}</strong> yet.
            {uncoveredSuggestion.nearest && (
              <>
                {" "}
                Nearest covered ZIP:{" "}
                <button
                  type="button"
                  data-testid="map-zip-search-nearest"
                  onClick={() => {
                    // F.4 — funnel event: clicked the nearest-covered hint.
                    trackRecruitEvent("search_nearest_clicked", {
                      zip: uncoveredSuggestion.nearest,
                      source: "map_search",
                    });
                    onMatch?.(uncoveredSuggestion.nearest);
                  }}
                  style={{
                    color: "var(--pp-orange)",
                    fontWeight: 600,
                    textDecoration: "underline",
                  }}
                >
                  {uncoveredSuggestion.nearest}
                </button>
                .
              </>
            )}
          </div>
          {!waitlistOpen ? (
            <button
              type="button"
              data-testid="map-zip-search-waitlist-cta"
              onClick={() => setWaitlistOpen(true)}
              className="mt-2 text-sm font-semibold"
              style={{
                color: "var(--pp-orange)",
                textDecoration: "underline",
              }}
            >
              Notify me when PooPluckers launches in {uncoveredSuggestion.zip} →
            </button>
          ) : (
            <form
              onSubmit={submitWaitlist}
              className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-center"
              data-testid="map-zip-search-waitlist-form"
            >
              <input
                type="email"
                value={waitlistEmail}
                onChange={(e) => {
                  setWaitlistEmail(e.target.value);
                  if (waitlistError) setWaitlistError("");
                }}
                placeholder="your@email.com"
                aria-label={`Email for ${uncoveredSuggestion.zip} waitlist`}
                data-testid="map-zip-search-waitlist-email-input"
                className="rounded-md px-3 py-2 flex-1"
                style={{
                  border: "1px solid var(--pp-line)",
                  background: "var(--pp-bg)",
                  color: "var(--pp-text)",
                  fontSize: 14,
                }}
              />
              <button
                type="submit"
                disabled={waitlistSubmitting}
                data-testid="map-zip-search-waitlist-submit"
                className="rounded-md px-3 py-2 text-sm font-semibold"
                style={{
                  background: "var(--pp-orange)",
                  color: "white",
                  opacity: waitlistSubmitting ? 0.6 : 1,
                }}
              >
                {waitlistSubmitting ? "Adding…" : "Notify me"}
              </button>
            </form>
          )}
          {waitlistError && (
            <div
              className="mt-2 text-sm"
              style={{ color: "#B91C1C" }}
              data-testid="map-zip-search-waitlist-error"
            >
              {waitlistError}
            </div>
          )}
        </div>
      )}
      {waitlistSubmitted && (
        <div
          className="mt-3 rounded-md p-3"
          style={{
            background: "rgba(91, 165, 100, 0.10)",
            border: "1px solid rgba(91, 165, 100, 0.30)",
            color: "var(--pp-text)",
          }}
          data-testid="map-zip-search-waitlist-success"
        >
          <strong>You&rsquo;re on the list.</strong>{" "}
          We&rsquo;ll email you when PooPluckers launches in{" "}
          <strong>{uncoveredSuggestion?.zip}</strong>.
        </div>
      )}
    </div>
  );
}
